import { I18n } from 'react-redux-i18n';

/**
 * Known on-the-wire `source` (Origin) values, shared across donation and the
 * Fundraiser/Team/Collection/Sale/Subscription view screens. Labels live under
 * the `common.origin` i18n namespace.
 */
export const ORIGIN_SOURCES = [
  'gr-public',
  'gr-admin',
  'gr-active-int',
  'register-now'
] as const;

export type OriginSource = (typeof ORIGIN_SOURCES)[number];

/**
 * Origin label context. The page entity screens (Fundraiser/Team/Collection)
 * relabel some sources (e.g. `gr-public` → "Fundraising flow"); donations,
 * sales and subscriptions use the default labels.
 */
export type OriginVariant = 'default' | 'page';

const isKnownOrigin = (source?: string | null): source is OriginSource =>
  !!source && (ORIGIN_SOURCES as readonly string[]).includes(source);

/** Per-source label overrides for the `page` variant. */
const PAGE_LABEL_KEYS: Partial<Record<OriginSource, string>> = {
  'gr-public': 'common.origin.page.gr-public'
};

/**
 * Returns the localized Origin label for a `source` value, or null when the
 * value is absent or not a recognised origin (so callers render nothing rather
 * than a raw i18n key). Pass `variant: 'page'` on the page entity screens to
 * pick up their relabelled origins.
 */
export const getOriginLabel = (
  source?: string | null,
  variant: OriginVariant = 'default'
): string | null => {
  if (!isKnownOrigin(source)) {
    return null;
  }
  const overrideKey = variant === 'page' ? PAGE_LABEL_KEYS[source] : undefined;
  return I18n.t(overrideKey || `common.origin.${source}`);
};
